import moment from 'moment';
import { getTranslation } from '../providers/TranslationProvider';

// in inline booking, mutates state. name is misleading
// const assessTodayAvailable = () => {
// is today available
// is the first day in the calendar today and
// is it available
// we did not update this funciton when we changed the
// data structure of the calendar to contain additional
// };

const assessQueryParamsForMessage = (location) => {
  const { message } = location.search();

  return message;
};

const findTimeSlot = (timeslot, reservationsByServicePeriod) => {
  let notifiedReservation = null;
  for (let i = 0; i < reservationsByServicePeriod.length; i += 1) {
    notifiedReservation = reservationsByServicePeriod[i]?.available.find(
      (resy) => (
        moment(
          resy.time.substring(11), 'HH:mm:ss'
        )
          .isSame(moment(timeslot, 'HH:mm:ss'),
            'minutes')
      )
    );
    if (notifiedReservation) {
      break;
    }
  }
  return notifiedReservation;
};

const findTimeSlotInRange = (start, end, reservationsByServicePeriod) => {
  let notifiedReservation = null;
  const timeFormat = 'HH:mm:ss';
  const startMoment = moment(start.substring(11), timeFormat);
  const endMoment = moment(end.substring(11), timeFormat);
  for (let i = 0; i < reservationsByServicePeriod.length; i += 1) {
    notifiedReservation = reservationsByServicePeriod[i]?.available?.find(
      (resy) => (
        moment(
          resy.time.substring(11), timeFormat
        )
          .isBetween(startMoment, endMoment, 'minutes', [])
      )
    );
    if (notifiedReservation) {
      break;
    }
  }
  return notifiedReservation;
};

const guestSingular = getTranslation({ translationKey: 'guest', fallback: 'guest' });
const guestsPlural = getTranslation({ translationKey: 'guests', fallback: 'guests' });

const suffix = {
  singular: guestSingular.toLowerCase(),
  plural: guestsPlural.toLowerCase(),
};

function generateQuantityOptions(min = 1, max = 20, isLargePartyConfigured) {
  const quantityOptions = [];

  for (let i = min; i <= max; i += 1) {
    quantityOptions.push({ value: i, label: `${i} ${(i === 1) ? suffix.singular : suffix.plural}` });
  }

  if (isLargePartyConfigured) {
    // Add one more quantity option for large parties if config says so
    const largePartySize = max + 1;
    quantityOptions.push({ value: largePartySize, label: `${largePartySize}+ ${suffix.plural}`, largeParty: true });
  }

  return quantityOptions;
}

// assessReservations -> hasAvailablity
const hasAvailablity = (reservations) => reservations.some((resy) => resy.available.length > 0);

function assessNotifyParams($location, openNotify) {
  const { notifyService } = $location.search();

  if (typeof (notifyService) === 'string') {
    openNotify(notifyService);
  }
  $location.search('notifyService', null).replace();
}

/**
* Notify 1: tableConfigId
* There are 2 potential paths:
* 1) tableConfigId matches a reservation's config.token (config.id)
* 2) tableConfigId does not match any reservation's config.token,
*    so we will display a message accordingly
*/
function assessTableConfigId({
  queryParams, reservationsByServicePeriod, selectResy, setNotifyData,
}) {
  let notifiedReservation = null;

  const notifiedTableConfigId = queryParams.tableConfigId;

  reservationsByServicePeriod.some((sp) => {
    notifiedReservation = sp.available.find((resy) => resy.token === notifiedTableConfigId);
    return !!notifiedReservation;
  });

  if (notifiedReservation) {
    selectResy({ tableConfigId: notifiedTableConfigId }, notifiedReservation);
  } else {
    setNotifyData({
      shouldShowTableConfigIdNotFound: true,
      shouldShowTimeslotNotFound: false,
      date: null,
      time: null,
    });
  }
}
/**
 * Notify 2: timeslot, request_start, request_end, notify_request_id
 * There are 3 potential paths:
 * 1) timeslot matches a reservation (this does not in and of itself allow us to match a specific table type)
 * 2) timeslot does not match a reservation, but we have reservations within the bounds set by request_start & request_end
 * 3) the first 2 paths are not valid, aka the timeslot notified is no longer present nor are other options within the desired range,
 *    so we will display a message accordingly
 */
function assessTimeslot({
  queryParams, reservationsByServicePeriod, selectResy, setNotifyData,
}) {
  if (!queryParams.timeslot) {
    return;
  }

  let notifiedReservation = null;

  /**
   * 1) If a timeslot parameter is present and a matching timeslot is available in reservations
   */
  notifiedReservation = findTimeSlot(queryParams.timeslot, reservationsByServicePeriod);

  /**
   * 2) If a timeslot parameter is present and a matching timeslot is NOT available in reservations
   *    => then use request_start & request_end to define a range and see if a reservation is available
   */
  if (!notifiedReservation && typeof (queryParams.request_start) === 'string' && typeof (queryParams.request_end) === 'string') {
    notifiedReservation = findTimeSlotInRange(queryParams.request_start, queryParams.request_end, reservationsByServicePeriod);
  }

  if (notifiedReservation) {
    const notifyConfig = {
      timeslot: queryParams.timeslot,
      request_start: queryParams.request_start,
      request_end: queryParams.request_end,
      notify_request_id: queryParams.notify_request_id,
    };

    selectResy(notifyConfig, notifiedReservation);

    /**
     * 3) If a timeslot parameter is present and a matching timeslot is NOT available in reservations
     *    AND no timeslots are available within the range
     *    => then display a message
     */
  } else {
    setNotifyData({
      shouldShowTableConfigIdNotFound: false,
      shouldShowTimeslotNotFound: true,
      date: moment(queryParams.date),
      time: moment(queryParams.request_start).format('HH:mm'),
      endTime: moment(queryParams.request_end).format('HH:mm'),
    });
  }
}

/**
 * Find a particular slot from reservations array
 * @param {*} slotsInfo - all available slots data.
 * @param {*} token - each slot has it's own token
 * @returns null/undefined if not found or the slot data if found
 */
function findSlotFromSlots(slotsInfo, token, exclusiveId) {
  let slot = null;
  for (let slotsCounter = 0; slotsCounter < slotsInfo.length; slotsCounter++) {
    const selectedslotinfo = slotsInfo[slotsCounter]?.available ?? slotsInfo[slotsCounter]?.slots ?? slotsInfo[slotsCounter];
    if (slot) break;
    else if (Array.isArray(selectedslotinfo)) slot = selectedslotinfo.find((item) => item?.token === token && item?.exclusiveId === exclusiveId) || null;
    else if (selectedslotinfo?.token === token && selectedslotinfo?.exclusiveId === exclusiveId) slot = selectedslotinfo;
  }
  return slot;
}

export {
  assessQueryParamsForMessage,
  findTimeSlot,
  findTimeSlotInRange,
  hasAvailablity,
  generateQuantityOptions,
  assessNotifyParams,
  assessTableConfigId,
  assessTimeslot,
  findSlotFromSlots,
};
